import { createSelector } from 'reselect';
import { produce } from 'immer';
import { find } from 'lodash-es';

import * as service from '../../services/soil-pending-fields-service';
import { asyncAction } from './async-action';
import { ScopedType, ScopedAsyncType } from './scoped-type';

const AsyncType = ScopedAsyncType('SOIL_PENDING_FIELDS');
const Type = ScopedType('SOIL_PENDING_FIELDS');

const SLICE = 'soilPendingFields';
const SELECT_FIELDS = Type('SELECT_FIELDS');
const UPDATE_DELAY_REASON = AsyncType('UPDATE_DELAY_REASON');
const LOAD_FIELDS = AsyncType('LOAD_FIELDS');
const LOAD_DELAY_REASONS = AsyncType('LOAD_DELAY_REASONS');
const APPROVE_FIELDS = AsyncType('APPROVE_FIELDS');
const REJECT_FIELDS = AsyncType('REJECT_FIELDS');
const REJECT_POINT = AsyncType('REJECT_POINT');

export const selectors = {
  isWorking: (state) => state[SLICE].isWorking,
  hasFailed: (state) => state[SLICE].error,
  failures: (state) => state[SLICE].errors,
  fields: (state) => state[SLICE].fields,
  delayReasons: (state) => state[SLICE].delayReasons,

  soilPoints: createSelector(
    (state) => state[SLICE].fields,
    (fields) => (fields || []).flatMap((f) => f.soilPoints),
  ),

  selectedFields: createSelector(
    (state) => state[SLICE].fields,
    (fields) => (fields || []).filter((f) => f.isSelected),
  ),
};

export const actions = {
  updateDelayReason,
  setSelectedFields,
  loadFields,
  loadDelayReasons,
  approveFields,
  rejectFields,
  rejectPoint,
};

const reducerHandlers = {
  [SELECT_FIELDS]: reduceSelectFields,
  [UPDATE_DELAY_REASON.start]: reduceUpdateDelayReasonStart,
  [UPDATE_DELAY_REASON.failure]: reduceUpdateDelayReasonFailed,
  [LOAD_FIELDS.start]: reduceLoadFieldsStart,
  [LOAD_FIELDS.complete]: reduceLoadFieldsComplete,
  [LOAD_FIELDS.failure]: reduceLoadFieldsFailed,
  [LOAD_DELAY_REASONS.complete]: reduceLoadDelayReasonsComplete,
  [APPROVE_FIELDS.start]: reduceApproveFieldsStart,
  [APPROVE_FIELDS.failure]: reduceApproveFieldsFailed,
  [APPROVE_FIELDS.complete]: reduceApproveFieldsComplete,
  [REJECT_FIELDS.start]: reduceRejectFieldsStart,
  [REJECT_FIELDS.failure]: reduceRejectFieldsFailed,
  [REJECT_FIELDS.complete]: reduceRejectFieldsComplete,
  [REJECT_POINT.start]: reduceRejectPointStart,
  [REJECT_POINT.failure]: reduceRejectPointFailed,
  [REJECT_POINT.complete]: reduceRejectPointComplete,
};

export const reducers = {
  [SLICE]: (state = { isLoading: true }, { type, payload } = {}) => {
    if (!reducerHandlers[type]) return state;
    return produce(state, (draft) => reducerHandlers[type](draft, payload));
  },
};

function updateDelayReason(aaid, reason) {
  return asyncAction(
    UPDATE_DELAY_REASON,
    () => service.updateDelayReason(aaid, reason),
    { startPayload: { aaid, reason }, failurePayload: { aaid } },
  );
}

function setSelectedFields(ids) {
  return { type: SELECT_FIELDS, payload: ids };
}

function loadFields() {
  return asyncAction(LOAD_FIELDS, () => service.getFields());
}

function loadDelayReasons() {
  return asyncAction(LOAD_DELAY_REASONS, () => service.getDelayReasons());
}

function approveFields(fields) {
  return asyncAction(APPROVE_FIELDS, () => service.approveFields(fields));
}

function rejectFields(fields) {
  return asyncAction(REJECT_FIELDS, () =>
    service.rejectFields(fields).then(() => fields),
  );
}

function rejectPoint(point) {
  return asyncAction(
    REJECT_POINT,
    () => service.rejectPoint(point).then(() => point),
    { startPayload: point, failurePayload: point },
  );
}

function reduceSelectFields(state, selectedIds) {
  state.fields.forEach((field) => {
    field.isSelected = selectedIds.includes(field.id);
  });
}

function reduceUpdateDelayReasonStart(state, { aaid, reason }) {
  delete state.error;
  const field = find(state.fields, { aaid });
  field.previousDelayReasonId = field.delayReasonId;
  field.delayReasonId = reason || null;
}

function reduceUpdateDelayReasonFailed(state, { aaid }) {
  state.error = true;
  const field = find(state.fields, { aaid });
  field.delayReasonId = field.previousDelayReasonId;
}

function reduceLoadFieldsStart(state) {
  delete state.error;
  delete state.fields;
}

function reduceLoadFieldsComplete(state, fields) {
  state.fields = fields;
}

function reduceLoadFieldsFailed(state, error) {
  state.fields = [];
  state.error = error.message || error;
}

function reduceLoadDelayReasonsComplete(state, reasons) {
  state.delayReasons = reasons;
}

function reduceApproveFieldsStart(state) {
  delete state.error;
  state.isWorking = true;
}

function reduceApproveFieldsComplete(state, results) {
  delete state.isWorking;
  state.errors = [];

  results.forEach(({ aaid, error }) => {
    const field = state.fields.find((f) => f.aaid === aaid);
    field.isSelected = Boolean(error);
    field.isRunning = !error;

    if (error) {
      state.error = `We had some trouble approving one or more fields. Please try again or contact support if the problem persists.`;
      state.errors.push(error);
    }
  });
}

function reduceApproveFieldsFailed(state, error) {
  delete state.isWorking;
  state.error = error.message || error;
}

function reduceRejectFieldsStart(state) {
  delete state.error;
  state.isWorking = true;
}

function reduceRejectFieldsComplete(state, fields) {
  delete state.isWorking;
  const ids = fields.map((field) => field.id);
  state.fields = state.fields.filter((field) => !ids.includes(field.id));
}

function reduceRejectFieldsFailed(state, error) {
  delete state.isWorking;
  state.error = error.message || error;
}

function getAllSoilPoints(state) {
  return state.fields.flatMap((field) => field.soilPoints);
}

function reduceRejectPointStart(state, rejecting) {
  delete state.error;
  getAllSoilPoints(state).forEach((point) => {
    if (point.objectId === rejecting.objectId) point.isRejecting = true;
  });
}

function reduceRejectPointComplete(state, rejected) {
  state.fields.forEach((field) => {
    if (find(field.soilPoints, { objectId: rejected.objectId })) {
      field.soilPoints = field.soilPoints.filter(
        (p) => p.objectId !== rejected.objectId,
      );
    }
  });
}

function reduceRejectPointFailed(state, failed) {
  state.error = true;
  getAllSoilPoints(state).forEach((point) => {
    if (point.objectId === failed.objectId) point.isRejecting = false;
  });
}
